// ═══════════════════════════════════════════════════════════
// RESULTS CONTENT MODEL
// All display content, messaging, styles, and CTA variants
// for the PII Finder results view and lead capture gate.
// Edit here to tune messaging, add fields, or change CTAs.
// ═══════════════════════════════════════════════════════════

import {
  Database, ShieldAlert, AlertTriangle, CheckCircle, Eye,
  Lock, Send, Lightbulb, Inbox, HardDrive, Activity, Share2,
  Sparkles, Building2, User, Users, Briefcase, FileText,
  Shield, ChevronRight, Download
} from 'lucide-react';

// ──────────────────────────────────────────────────────────
// SEVERITY & CONFIDENCE STYLE MAPS
// ──────────────────────────────────────────────────────────

export const SEVERITY_STYLES = {
  high:   { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  low:    { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
};

export const CONFIDENCE_STYLES = {
  high:   { border: 'border-l-[3px] border-l-[#003366]', badge: 'bg-[#003366] text-white' },
  medium: { border: 'border-l-[3px] border-l-[#008080]', badge: 'bg-[#008080] text-white' },
  low:    { border: 'border-l-[3px] border-l-slate-300', badge: 'bg-slate-200 text-slate-600' },
};

export const PRIORITY_STYLES = {
  critical: 'bg-red-100 text-red-700 border border-red-200',
  high:     'bg-amber-100 text-amber-700 border border-amber-200',
  medium:   'bg-blue-100 text-blue-700 border border-blue-200',
};

// ──────────────────────────────────────────────────────────
// TOUCHPOINT DISPLAY METADATA
// ──────────────────────────────────────────────────────────

export const TOUCHPOINT_META = {
  collect: { icon: Inbox,     label: 'Collected At', bg: 'bg-blue-50',    border: 'border-blue-200',    iconBg: 'bg-blue-100 text-blue-700',    text: 'text-blue-800' },
  store:   { icon: HardDrive, label: 'Stored In',    bg: 'bg-purple-50',  border: 'border-purple-200',  iconBg: 'bg-purple-100 text-purple-700',  text: 'text-purple-800' },
  use:     { icon: Activity,  label: 'Used For',     bg: 'bg-emerald-50', border: 'border-emerald-200', iconBg: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-800' },
  share:   { icon: Share2,    label: 'Shared With',  bg: 'bg-orange-50',  border: 'border-orange-200',  iconBg: 'bg-orange-100 text-orange-700',  text: 'text-orange-800' },
};

// ──────────────────────────────────────────────────────────
// SECTION LABELS (icon + text headings in results)
// ──────────────────────────────────────────────────────────

export const SECTION_LABELS = {
  categories: { icon: Database, labelFn: (count) => `Personal Data Landscape \u2014 ${count} Categories Likely Handled` },
  topImpacts: { icon: ShieldAlert, label: 'Top 3 DPDP Impact Areas' },
  visibilityGap: { icon: Eye, label: 'Business Visibility Gap' },
  touchpoints: { icon: Share2, label: 'Business Data Touchpoints' },
  blindSpots: { icon: AlertTriangle, labelFn: (count) => `Blind Spots Detected \u2014 ${count} Found` },
  nextSteps: { icon: CheckCircle, label: 'What To Do Next' },
};

// ──────────────────────────────────────────────────────────
// SHARE SNAPSHOT
// ──────────────────────────────────────────────────────────

export const SNAPSHOT_CONTENT = {
  buttonLabel: 'Share My DPDP Snapshot',
  loadingLabel: 'Generating Snapshot...',
  helperText: 'Download as image to share with your team',
  headerTitle: 'DPDP Impact Snapshot',
  headerSubtitle: 'Generated by CX0  \u00B7  codexero.ai',
  footerText: 'dpdp.codexero.ai  \u00B7  Run the full CX0 DPDP Readiness Assessment for a complete compliance roadmap',
  fileName: 'DPDP-Impact-Snapshot.png',
};

// ──────────────────────────────────────────────────────────
// LEAD CAPTURE GATE (pre-submission)
// ──────────────────────────────────────────────────────────

export const LEAD_GATE = {
  badge: { icon: Shield, label: 'Continue Your Assessment' },
  headline: 'Get Your Detailed DPDP Impact Summary',
  description: "You've seen the highlights. Unlock the full picture — data flow mapping, blind spot analysis, and a prioritised action plan tailored to your organisation.",

  // Preview cards that show what's gated
  previewCards: [
    {
      icon: Share2,
      title: 'Business Data Footprint',
      description: 'See exactly where personal data flows — from collection points through storage to third-party sharing.',
    },
    {
      icon: AlertTriangle,
      title: 'Blind Spot Analysis',
      descriptionFn: (count) => `${count} hidden risk${count !== 1 ? 's' : ''} detected in your data landscape that need attention.`,
    },
    {
      icon: CheckCircle,
      title: 'Next-Step Recommendations',
      description: 'A prioritised action checklist specific to your industry and data profile.',
    },
  ],

  // Form section
  form: {
    sectionIcon: FileText,
    sectionTitle: 'Tell us about your organisation',
    sectionDescription: 'So we can personalise your impact summary and follow-up guidance.',
    privacyNote: 'Your information is secure and will only be used to deliver your DPDP assessment results. No spam.',
    submitLabel: 'Unlock Full DPDP Impact Summary',
  },
};

// ──────────────────────────────────────────────────────────
// LEAD FORM FIELDS
// ──────────────────────────────────────────────────────────

export const LEAD_FORM_FIELDS = {
  name: {
    icon: User,
    label: 'Full Name',
    placeholder: 'e.g. Rahul Sharma',
    required: true,
    testId: 'lead-input-name',
  },
  email: {
    icon: Send,
    label: 'Work Email',
    placeholder: 'you@company.com',
    type: 'email',
    required: true,
    testId: 'lead-input-email',
  },
  company_name: {
    icon: Building2,
    label: 'Company Name',
    placeholder: 'e.g. Acme Technologies Pvt. Ltd.',
    required: true,
    testId: 'lead-input-company',
  },
  company_size: {
    icon: Users,
    label: 'Company Size',
    required: false,
    testId: 'lead-select-company-size',
    type: 'select',
    options: [
      { value: '1-10', label: '1\u201310 employees' },
      { value: '11-50', label: '11\u201350 employees' },
      { value: '51-200', label: '51\u2013200 employees' },
      { value: '201-500', label: '201\u2013500 employees' },
      { value: '501-1000', label: '501\u20131,000 employees' },
      { value: '1001-5000', label: '1,001\u20135,000 employees' },
      { value: '5000+', label: '5,000+ employees' },
    ],
  },
  role: {
    icon: Briefcase,
    label: 'Your Role',
    required: false,
    testId: 'lead-select-role',
    type: 'select',
    options: [
      { value: 'cxo', label: 'CXO / Founder' },
      { value: 'vp_director', label: 'VP / Director' },
      { value: 'head_of_dept', label: 'Head of Department' },
      { value: 'manager', label: 'Manager' },
      { value: 'legal_compliance', label: 'Legal / Compliance' },
      { value: 'it_security', label: 'IT / Security' },
      { value: 'consultant', label: 'Consultant / Advisor' },
      { value: 'other', label: 'Other' },
    ],
  },
};

// ──────────────────────────────────────────────────────────
// POST-SUBMISSION CTA
// ──────────────────────────────────────────────────────────

export const RESULTS_CTA = {
  badge: { icon: Sparkles, label: 'Next Step' },
  headline: 'Know Your DPDP Readiness in Hours, Not Weeks.',
  description: 'Get a clear view of your compliance gaps and risk exposure \u2014 and receive a practical',
  descriptionBold: '12\u201318 month remediation roadmap',
  descriptionEnd: ', tailored to your organisation.',
  supportLine: '15 Assessment pillars. 83 questions. No technical expertise required.',
  ctaLabel: 'Start Executive Readiness Assessment',
  ctaHref: '/request-access',
  ctaTestId: 'results-full-assessment-cta',
};

// ──────────────────────────────────────────────────────────
// DOWNLOAD FULL SUMMARY
// ──────────────────────────────────────────────────────────

export const DOWNLOAD_SUMMARY = {
  buttonLabel: 'Download Full Impact Summary',
  loadingLabel: 'Preparing Download...',
  helperText: 'Complete summary with data touchpoints and action plan',
  headerTitle: 'DPDP Impact Summary',
  footerText: 'dpdp.codexero.ai  \u00B7  Run the full CX0 DPDP Readiness Assessment for a complete compliance roadmap',
  fileNameFn: (company) => `DPDP-Impact-Summary${company ? '-' + company.replace(/\s+/g, '-') : ''}.png`,
};

// ──────────────────────────────────────────────────────────
// CONFIRMATION FOOTER
// ──────────────────────────────────────────────────────────

export const CONFIRMATION = {
  messageFn: (email) => `Impact summary sent to ${email}`,
  followUp: 'Our team will reach out with your personalised CX0 assessment path.',
};

// ──────────────────────────────────────────────────────────
// SCORE HERO BAND LABELS
// ──────────────────────────────────────────────────────────

export const SCORE_BAND = {
  headerLabel: 'Your DPDP Impact Analysis',
  visibilityGapLabel: 'Visibility Gap',
  sdfBadge: 'LIKELY SDF',
  statsFn: (categories, flags, blindSpots, touchpoints) => [
    { value: categories, label: 'Data Categories' },
    { value: flags, label: 'Compliance Flags' },
    { value: blindSpots, label: 'Blind Spots' },
    { value: touchpoints, label: 'Touchpoints Mapped' },
  ],
};

// ──────────────────────────────────────────────────────────
// EXPANDED SUMMARY LABELS
// ──────────────────────────────────────────────────────────

export const SUMMARY_CARDS = {
  labels: [
    { key: 'score', label: 'Impact Score' },
    { key: 'categories', label: 'Data Categories' },
    { key: 'flags', label: 'Compliance Flags' },
    { key: 'gap', label: 'Visibility Gap' },
  ],
};

// ──────────────────────────────────────────────────────────
// WATERMARK
// ──────────────────────────────────────────────────────────

export const WATERMARK = {
  prefix: 'Powered by',
  suffix: 'codexero.ai',
};
